import {
    type RequestConfigFromRtkq,
    serverWebApiBaseQuery
} from 'tradera-backend/http/webapi-rtk';
import type { Request, Response } from 'tradera-backend/http/types/http';
import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { isServer } from 'tradera/utils/nextjs';
import type {
    ItemCardViewModel,
    ResponseStatus
} from '../types/webapi-discover-generated';
import { wishlistAddRange } from 'tradera/state/items/reducer';
import { frontendBaseQueryWithReauth } from 'tradera/state/services/utils/webapi-frontend-base-query';

export type WithGetWishListableItems = {
    getWishListableItems: (data: unknown) => ItemCardViewModel[];
};

const isWithGetWishListableItems = (
    item: unknown
): item is WithGetWishListableItems =>
    (item as WithGetWishListableItems)?.getWishListableItems !== undefined;

const mustHandleWishListableItems = (input: string | object) =>
    typeof input === 'string';

const handleWishListableItems = async (
    input: unknown,
    api: BaseQueryApi,
    extraOptions: object,
    data: unknown
) => {
    // All endpoints must handle possible wishlistable items.
    // If no wishlistable items can come from the endpoint the implementation
    // of getWishListableItems should return a falsy value.
    if (!isWithGetWishListableItems(extraOptions)) {
        throw new Error(
            `Must handle possible wishlistable items from ${input}`
        );
    }

    const items = extraOptions.getWishListableItems(data);

    if (!items) {
        return;
    }

    api.dispatch(
        wishlistAddRange(items.filter((x) => x.inWishList).map((x) => x.itemId))
    );
};

type ServerState = {
    request: { request: Request; response: Response };
};

const isServerState = (state: unknown): state is ServerState =>
    isServer &&
    state !== null &&
    typeof state === 'object' &&
    'request' in state;

export type WebApiError = {
    data?: {
        responseStatus?: ResponseStatus;
    };
};

const doServerWebApiBaseQuery = (
    input: string | RequestConfigFromRtkq,
    state: ServerState
) => {
    const { request, response } = state.request;
    if (!request || !response) {
        // An error has occurred elsewhere and request and response
        // were reset. When this happens we don't want to cause more
        // errors which fills up logs by trying to make further requests.
        return Promise.resolve({
            error: { status: 400, data: 'No request or response in state' },
            data: undefined
        });
    }
    return serverWebApiBaseQuery(request, response, input);
};

export const isomorphicWebApiBaseQuery: BaseQueryFn<
    string | RequestConfigFromRtkq,
    unknown,
    WebApiError,
    object
> = async (
    input: string | RequestConfigFromRtkq,
    api: BaseQueryApi,
    extraOptions: object
) => {
    const state = api.getState();
    return isServerState(state)
        ? doServerWebApiBaseQuery(input, state)
        : (frontendBaseQueryWithReauth(input, api, extraOptions) as Promise<{
              data: unknown;
          }>);
};

export const isomorphicBaseQueryForWishListableItems = async (
    input: string | RequestConfigFromRtkq,
    api: BaseQueryApi,
    extraOptions: object
) => {
    const result = await isomorphicWebApiBaseQuery(input, api, extraOptions);

    if (mustHandleWishListableItems(input)) {
        const data = (await result).data;
        // There will be no data if the request failed.
        if (data) {
            await handleWishListableItems(input, api, extraOptions, data);
        }
    }
    return result;
};
